import { useEffect } from "react";
import { CONDITIONS } from "@/data/conditions";
import { TESTIMONIALS } from "@/data/testimonials";

const WA_BOOK = "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20would%20like%20to%20book%20an%20appointment";

const TITLE = "Best Physiotherapy Clinic in Sector 110 Gurugram | PhysioSewa — Expert Physio Near You";
const DESCRIPTION = "PhysioSewa — Advanced Physiotherapy & Rehabilitation in Sector 110 Gurugram. Restoring movement, rebuilding life. Pain management, neuro rehab, geriatric care, sports injuries, maternity care & manual therapy. Open 7 days, 9 AM – 9 PM. Call 9871053777 / 9217221357.";
const KEYWORDS = "physiotherapy clinic Gurugram, physiotherapist Sector 110, best physio Gurgaon, back pain treatment Gurugram, sports injury rehab Gurgaon, physiotherapy at home Gurugram, neuro rehab Gurugram, post surgical physiotherapy Gurgaon, geriatric physiotherapy Gurgaon, maternity physiotherapy Gurugram, dry needling Gurgaon, cupping therapy Gurugram, PhysioSewa, Dr Anusha Dubey physiotherapist";
const CANONICAL = "https://physiosewa.in/";

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "name": "PhysioSewa",
  "image": "https://physiosewa.in/logo.png",
  "url": "https://physiosewa.in",
  "telephone": ["+91-9871053777", "+91-9217221357"],
  "email": "info@physiosewa.in",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "B-71, 40 Feet Road, New Palam Vihar Phase 1, Near Dwarka Expressway",
    "addressLocality": "Gurugram Sector 110",
    "addressRegion": "Haryana",
    "postalCode": "122017",
    "addressCountry": "IN",
  },
  "geo": { "@type": "GeoCoordinates", "latitude": 28.4078, "longitude": 77.0389 },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday", "Tuesday", "Wednesday", "Thursday",
        "Friday", "Saturday", "Sunday"
      ],
      "opens": "09:00",
      "closes": "21:00",
    },
  ],
  "priceRange": "₹700 - ₹22500",
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "13" },
  "medicalSpecialty": [
    "Physiotherapy",
    "Pain Management",
    "Neurological Rehabilitation",
    "Geriatric Rehabilitation",
    "Orthopedic & Sports Rehabilitation",
    "Maternity Care",
    "Manual Therapy",
  ],
};

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  let createdHere = false;
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
    createdHere = true;
  }
  const prev = {};
  Object.keys(attrs).forEach((k) => { prev[k] = el.getAttribute(k); });
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return { el, prev, createdHere };
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let createdHere = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
    createdHere = true;
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, createdHere };
};

const PRICING_ROWS = [
  { svc: "At Home — 1 Session",   dur: "45 mins", price: "₹950" },
  { svc: "At Home — 7 Sessions",  dur: "45 mins", price: "₹6,300" },
  { svc: "At Home — 30 Sessions", dur: "45 mins", price: "₹24,000" },
  { svc: "At Clinic — 1 Session",   dur: "45 mins", price: "₹800" },
  { svc: "At Clinic — 7 Sessions",  dur: "45 mins", price: "₹5,250" },
  { svc: "At Clinic — 30 Sessions", dur: "45 mins", price: "₹19,500" },
];

// =============================================================================
// Main SEO page
// =============================================================================
export default function SeoPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;

    const handles = [
      upsertMeta('meta[name="description"]', { name: "description", content: DESCRIPTION }),
      upsertMeta('meta[name="keywords"]',    { name: "keywords",    content: KEYWORDS }),
      upsertMeta('meta[name="robots"]',      { name: "robots",      content: "index, follow" }),
      upsertMeta('meta[property="og:title"]',       { property: "og:title",       content: TITLE }),
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: DESCRIPTION }),
      upsertMeta('meta[property="og:url"]',         { property: "og:url",         content: CANONICAL }),
      upsertMeta('meta[property="og:type"]',        { property: "og:type",        content: "website" }),
    ];
    const linkHandle = upsertLink("canonical", CANONICAL);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "seo-page-jsonld";
    ld.text = JSON.stringify(JSON_LD);
    document.head.appendChild(ld);

    window.scrollTo(0, 0);

    // Restore previous head state on unmount so the SEO page's metadata
    // doesn't leak onto the rest of the SPA.
    return () => {
      document.title = prevTitle;
      handles.forEach(({ el, prev, createdHere }) => {
        if (createdHere) {
          el.remove();
        } else {
          Object.entries(prev).forEach(([k, v]) => {
            if (v == null) el.removeAttribute(k);
            else el.setAttribute(k, v);
          });
        }
      });
      if (linkHandle.createdHere) linkHandle.el.remove();
      else linkHandle.el.setAttribute("href", linkHandle.prev || "");
      const stale = document.getElementById("seo-page-jsonld");
      if (stale) stale.remove();
    };
  }, []);

  return (
    <main className="seo-page" data-testid="seo-page" style={{ background: "white", color: "var(--text-mid)" }}>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-16 md:py-24">
        {/* Top utility links — small, intentionally low-key */}
        <div className="text-xs mb-6" style={{ color: "var(--text-light)" }}>
          <a href="tel:+919871053777" style={{ color: "var(--primary)", fontWeight: 600 }}>📞 9871053777</a>
          <span className="mx-1">|</span>
          <a href="tel:+919217221357" style={{ color: "var(--primary)", fontWeight: 600 }}>9217221357</a>
          <span className="mx-2">·</span>
          <a href="mailto:info@physiosewa.in" style={{ color: "var(--primary)", fontWeight: 600 }}>info@physiosewa.in</a>
          <span className="mx-2">·</span>
          <a href="/" style={{ color: "var(--primary)", fontWeight: 600 }}>Home</a>
        </div>

        {/* SECTION 1 — H1 */}
        <section data-testid="seo-section-1">
          <h1
            className="heading-xl"
            style={{
              fontSize: "clamp(2.1rem, 4.4vw, 3.2rem)",
              color: "var(--text-dark)",
              lineHeight: 1.1,
            }}
          >
            Best Physiotherapy Clinic in Sector 110, Gurugram
          </h1>
          <p className="mt-5 text-base md:text-lg" style={{ lineHeight: 1.75 }}>
            PhysioSewa is a leading physiotherapy clinic located in Sector 110, Gurugram (Gurgaon),
            Haryana. Founded by Dr. Anusha Dubey (BPTh, MPTh — Senior Physiotherapist with 8+ years
            of clinical experience), PhysioSewa delivers personalised, evidence-based physiotherapy
            for patients of all ages across Gurugram. We have treated over 2000 patients and hold a
            5.0 Google rating from verified patients.
          </p>
        </section>

        {/* SECTION 2 — Services */}
        <section className="mt-16" data-testid="seo-section-2">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Physiotherapy Services in Gurugram
          </h2>
          <div className="mt-6 grid md:grid-cols-2 gap-x-10 gap-y-7">
            {CONDITIONS.map((c) => (
              <div key={c.slug}>
                <h3 className="text-lg font-bold" style={{ color: "var(--text-dark)" }}>{c.title}</h3>
                <p className="mt-1.5 text-sm" style={{ lineHeight: 1.65 }}>
                  {c.summary} {c.what?.[0]?.split(". ")[0]}.
                </p>
                <a href={`/services/${c.slug}`} className="mt-2 inline-block text-sm font-semibold" style={{ color: "var(--primary)" }}>
                  Read more about {c.title.toLowerCase()} →
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 3 — Why Choose */}
        <section className="mt-16" data-testid="seo-section-3">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Why Choose PhysioSewa for Physiotherapy in Gurugram?
          </h2>
          <div className="mt-5 space-y-5" style={{ lineHeight: 1.75 }}>
            <p>
              <strong style={{ color: "var(--text-dark)" }}>Personalised care:</strong> No two patients
              are alike, so no two treatment plans should be either. Every PhysioSewa plan is built
              around your specific condition, lifestyle and recovery goals — not a generic protocol.
            </p>
            <p>
              <strong style={{ color: "var(--text-dark)" }}>Advanced equipment:</strong> Our Sector 110
              clinic is equipped with the latest electrotherapy machines, therapeutic ultrasound,
              dry-needling tools and manual therapy aids — the same standard you would expect from a
              top-tier rehab centre.
            </p>
            <p>
              <strong style={{ color: "var(--text-dark)" }}>Home visit service:</strong> Can't travel?
              We come to you. PhysioSewa offers professional home physiotherapy visits across all
              major areas of Gurugram, ideal for post-surgical patients, elderly patients and
              neurological rehab.
            </p>
            <p>
              <strong style={{ color: "var(--text-dark)" }}>Evidence-based practice:</strong> Every
              treatment we deliver is grounded in international clinical guidelines and the latest
              physiotherapy research — not guesswork.
            </p>
          </div>
        </section>

        {/* SECTION 4 — Home Visit */}
        <section className="mt-16" data-testid="seo-section-4">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Physiotherapy at Home in Gurugram
          </h2>
          <p className="mt-5" style={{ lineHeight: 1.75 }}>
            PhysioSewa offers home physiotherapy visits across all major areas of Gurugram including
            Sector 110, Sector 56, Sector 82, Sector 102, DLF Phase 1–5, Golf Course Road, Sohna
            Road, Palam Vihar, Dwarka Expressway areas and New Palam Vihar. Our certified
            physiotherapists bring clinic-quality care directly to your home.
          </p>
        </section>

        {/* SECTION 5 — Conditions */}
        <section className="mt-16" data-testid="seo-section-5">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Conditions We Treat at Our Gurugram Physiotherapy Clinic
          </h2>
          <p className="mt-5" style={{ lineHeight: 1.75 }}>
            Back pain and neck pain treatment in Gurugram, sciatica treatment Gurgaon, cervical
            spondylosis treatment Sector 110, sports injury rehabilitation Gurugram, ACL tear recovery
            Gurgaon, post-surgical physiotherapy Gurugram, knee replacement rehab Gurgaon, stroke
            rehabilitation Gurugram, Parkinson's disease physiotherapy Gurgaon, women's health
            physiotherapy Gurugram, antenatal physiotherapy Sector 110, paediatric physiotherapy
            Gurugram, cerebral palsy physiotherapy Gurgaon.
          </p>
        </section>

        {/* SECTION 6 — Pricing */}
        <section className="mt-16" data-testid="seo-section-6">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Physiotherapy Pricing in Gurugram
          </h2>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--primary-pale)" }}>
                  <th className="text-left p-3 font-semibold" style={{ color: "var(--text-dark)" }}>Service</th>
                  <th className="text-left p-3 font-semibold" style={{ color: "var(--text-dark)" }}>Duration</th>
                  <th className="text-left p-3 font-semibold" style={{ color: "var(--text-dark)" }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_ROWS.map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(168,213,181,0.45)" }}>
                    <td className="p-3" style={{ color: "var(--text-dark)" }}>{r.svc}</td>
                    <td className="p-3">{r.dur}</td>
                    <td className="p-3 font-semibold" style={{ color: "var(--primary)" }}>{r.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* SECTION 7 — Book via WhatsApp */}
        <section className="mt-16" data-testid="seo-section-7">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Book a Physiotherapy Appointment in Gurugram
          </h2>
          <p className="mt-3" style={{ lineHeight: 1.7 }}>
            Tap the button below to message PhysioSewa on WhatsApp. We confirm bookings within 30
            minutes during clinic hours (9:00 AM – 9:00 PM, 7 days a week).
          </p>
          <div
            className="mt-6 rounded-2xl p-5 md:p-7 text-center"
            style={{
              background: "linear-gradient(135deg, #1B3A2D 0%, #6DBF8A 100%)",
              color: "white",
              boxShadow: "0 6px 22px rgba(27,58,45,0.16)",
            }}
          >
            <a
              href={WA_BOOK}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-wa-btn"
              data-testid="seo-whatsapp-btn"
            >
              <i className="fa-brands fa-whatsapp"></i> Chat With Us on WhatsApp
            </a>
            <div className="mt-4 text-[14px]" style={{ color: "rgba(255,255,255,0.85)" }}>
              Or call <a href="tel:+919871053777" style={{ color: "white", textDecoration: "underline", fontWeight: 600 }}>9871053777</a>
              <span className="mx-2">·</span>
              <a href="tel:+919217221357" style={{ color: "white", textDecoration: "underline", fontWeight: 600 }}>9217221357</a>
            </div>
          </div>
        </section>

        {/* SECTION 8 — Find us */}
        <section className="mt-16" data-testid="seo-section-8">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Find PhysioSewa — Physiotherapy Clinic Near Sector 110
          </h2>
          <ul className="mt-5 space-y-2" style={{ lineHeight: 1.75 }}>
            <li><strong style={{ color: "var(--text-dark)" }}>Address:</strong> B-71, 40 Feet Road, New Palam Vihar Phase 1, Near Dwarka Expressway, Sector 110, Gurugram 122017, Haryana</li>
            <li><strong style={{ color: "var(--text-dark)" }}>Phone:</strong> <a href="tel:+919871053777" style={{ color: "var(--primary)" }}>9871053777</a> | <a href="tel:+919217221357" style={{ color: "var(--primary)" }}>9217221357</a></li>
            <li><strong style={{ color: "var(--text-dark)" }}>Email:</strong> <a href="mailto:info@physiosewa.in" style={{ color: "var(--primary)" }}>info@physiosewa.in</a></li>
            <li><strong style={{ color: "var(--text-dark)" }}>Website:</strong> www.physiosewa.in</li>
            <li><strong style={{ color: "var(--text-dark)" }}>Hours:</strong> Mon–Sun: 9:00 AM – 9:00 PM (Open 7 days a week)</li>
          </ul>
          <div className="mt-6">
            <iframe
              title="PhysioSewa Sector 110 Gurugram"
              src="https://maps.google.com/maps?q=PhysioSewa+B-71+40+Feet+Road+New+Palam+Vihar+Phase+2+Sector+110+Gurugram+Haryana+122017&t=&z=16&ie=UTF8&iwloc=B&output=embed"
              width="100%"
              height="360"
              style={{ border: 0, borderRadius: "16px", boxShadow: "0 4px 18px rgba(0,0,0,0.08)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="seo-map"
            />
          </div>
        </section>

        {/* SECTION 9 — Reviews */}
        <section className="mt-16" data-testid="seo-section-9">
          <h2 className="heading-lg" style={{ color: "var(--primary)", fontSize: "clamp(1.7rem, 3vw, 2.2rem)" }}>
            Patient Reviews
          </h2>
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                className="p-5 rounded-2xl"
                style={{ background: "var(--primary-pale)", border: "1px solid rgba(168,213,181,0.45)" }}
                data-testid={`seo-review-${i}`}
              >
                <div className="text-sm" style={{ color: "var(--gold)", marginBottom: "0.4rem" }}>
                  {"★★★★★".slice(0, t.stars)}
                </div>
                <p className="text-sm" style={{ color: "var(--text-mid)", lineHeight: 1.7 }}>"{t.text}"</p>
                <div className="mt-3 text-sm font-semibold" style={{ color: "var(--text-dark)" }}>
                  — {t.name}, <span style={{ color: "var(--text-light)", fontWeight: 500 }}>{t.loc}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <p className="mt-16 text-xs text-center" style={{ color: "var(--text-light)" }}>
          © PhysioSewa · Sector 110, Gurugram · <a href="/" style={{ color: "var(--primary)" }}>Visit homepage</a>
        </p>
      </div>
    </main>
  );
}
